import apiClient from './apiClient';

export interface ReportMetadata {
  id: string;
  name: string;
  description: string;
  category: 'FINANCIAL' | 'OPERATIONS' | 'COMPLIANCE' | 'CUSTOM';
  parameters: ReportParameter[];
  lastRun?: string;
  frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
}

export interface ReportParameter {
  name: string;
  label: string;
  type: 'DATE' | 'DATE_RANGE' | 'SELECT' | 'MULTISELECT' | 'BOOLEAN';
  required: boolean;
  options?: { value: string; label: string }[];
  defaultValue?: any;
}

export interface GenerateReportRequest {
  reportId: string;
  parameters: Record<string, any>;
  format: 'PDF' | 'EXCEL' | 'CSV';
  email?: string;
}

export interface ScheduledReport {
  id: string;
  reportId: string;
  reportName: string;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  nextRun: string;
  recipients: string[];
  parameters: Record<string, any>;
  format: 'PDF' | 'EXCEL' | 'CSV';
  enabled: boolean;
}

export interface CreateScheduledReportRequest {
  reportId: string;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  recipients: string[];
  parameters: Record<string, any>;
  format: 'PDF' | 'EXCEL' | 'CSV';
}

class ReportsService {
  /**
   * Get all available reports
   */
  async getAvailableReports(category?: string): Promise<ReportMetadata[]> {
    const response = await apiClient.get('/reports/available', {
      params: { category },
    });
    return response.data;
  }

  /**
   * Get report metadata by ID
   */
  async getReportMetadata(reportId: string): Promise<ReportMetadata> {
    const response = await apiClient.get(`/reports/${reportId}/metadata`);
    return response.data;
  }

  /**
   * Generate a report
   */
  async generateReport(request: GenerateReportRequest): Promise<Blob> {
    const response = await apiClient.post('/reports/generate', request, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  /**
   * Preview a report (returns first page or sample data)
   */
  async previewReport(reportId: string, parameters: Record<string, any>): Promise<any> {
    const response = await apiClient.post(`/reports/${reportId}/preview`, { parameters });
    return response.data;
  }

  /**
   * Get all scheduled reports
   */
  async getScheduledReports(): Promise<ScheduledReport[]> {
    const response = await apiClient.get('/reports/scheduled');
    return response.data;
  }

  /**
   * Create a scheduled report
   */
  async createScheduledReport(
    request: CreateScheduledReportRequest
  ): Promise<ScheduledReport> {
    const response = await apiClient.post('/reports/scheduled', request);
    return response.data;
  }

  /**
   * Update a scheduled report
   */
  async updateScheduledReport(
    id: string,
    request: Partial<CreateScheduledReportRequest>
  ): Promise<ScheduledReport> {
    const response = await apiClient.put(`/reports/scheduled/${id}`, request);
    return response.data;
  }

  /**
   * Delete a scheduled report
   */
  async deleteScheduledReport(id: string): Promise<void> {
    await apiClient.delete(`/reports/scheduled/${id}`);
  }

  /**
   * Enable/disable a scheduled report
   */
  async toggleScheduledReport(id: string, enabled: boolean): Promise<ScheduledReport> {
    const response = await apiClient.patch(`/reports/scheduled/${id}`, { enabled });
    return response.data;
  }

  /**
   * Run a scheduled report immediately
   */
  async runScheduledReport(id: string): Promise<void> {
    await apiClient.post(`/reports/scheduled/${id}/run`);
  }
}

export default new ReportsService();
